import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import {
  getSupportedImageMimeTypeFromFileName,
  isSupportedImageMimeType,
} from '@canvas/constants/multimedia';
import { isValidUuid } from '@canvas/model/board';

import { parsePositiveNumber } from './utils.ts';

type ConfluenceImageData = {
  fileId: string;
  sourceClientId: string;
  sourceCollection?: string;
  mimeType: string;
  nativeWidth: number;
  nativeHeight: number;
  width: number;
  height: number;
};

type ConfluenceImageParseResult =
  | { kind: 'notConfluenceImage' }
  | {
      kind: 'invalidConfluenceImage';
      unsupportedMediaMimeType: string;
      htmlWithoutImages: string;
    }
  | {
      kind: 'confluenceImage';
      images: ConfluenceImageData[];
      htmlWithoutImages: string;
    };

type ConfluenceImageNodeParseResult =
  | { kind: 'image'; image: ConfluenceImageData }
  | { kind: 'invalid'; mimeType?: string };

type ConfluenceImageNode =
  | {
      kind: 'media';
      mediaNode: HTMLElement;
      renderedImageNode: HTMLImageElement | null;
    }
  | { kind: 'renderedImage'; renderedImageNode: HTMLImageElement };

type ConfluenceImageAttributes = {
  fileId?: string;
  sourceClientId?: string;
  sourceCollection?: string;
  mimeType?: string;
  nativeWidth?: number;
  nativeHeight?: number;
  displayWidth?: number;
};

const CONFLUENCE_MEDIA_SELECTOR =
  '[data-node-type="media"][data-type="file"], [data-prosemirror-node-name="media"][data-type="file"]';
const CONFLUENCE_MEDIA_SINGLE_SELECTOR =
  '[data-node-type="mediaSingle"], [data-prosemirror-node-name="mediaSingle"]';
const CONFLUENCE_RENDERED_IMAGE_SELECTOR = 'img[data-fileid][src*="media-blob-url=true"]';
const UNSUPPORTED_MIXED_CONTENT_SELECTOR =
  'img, picture, video, audio, table, hr, iframe, object, embed, svg, canvas';

/**
 * Confluence images have two clipboard payload shapes: edit-mode copies store media
 * metadata on the media node, while view-mode copies may move some metadata to the
 * rendered image or its URL. Normalize both shapes here, preferring the media node.
 */
const getConfluenceImageAttributes = (
  node: ConfluenceImageNode,
): ConfluenceImageAttributes => {
  const mediaNode = node.kind === 'media' ? node.mediaNode : undefined;
  const imageNode = node.renderedImageNode;
  const mediaContainerNode = mediaNode?.closest<HTMLElement>(
    CONFLUENCE_MEDIA_SINGLE_SELECTOR,
  );
  const imageSource = imageNode?.getAttribute('src');
  const fragmentIndex = imageSource?.indexOf('#') ?? -1;
  const imageParams =
    imageSource && fragmentIndex >= 0
      ? new URLSearchParams(imageSource.slice(fragmentIndex + 1))
      : undefined;
  const mediaAttributes = mediaNode?.dataset;
  const imageAttributes = imageNode?.dataset;

  // View-mode copies may move the filename from the media node to the image URL.
  // Check both because an earlier value may lack an extension.
  const inferredMimeType = [mediaAttributes?.fileName, imageParams?.get('name')]
    .map(getSupportedImageMimeTypeFromFileName)
    .find((mimeType) => mimeType !== undefined);

  return {
    // Images with a Confluence media node use data-id; standalone images use data-fileid.
    fileId: mediaAttributes?.id || imageAttributes?.fileid || undefined,
    // Edit mode stores the client ID on the media node; view mode stores it in the URL.
    sourceClientId:
      mediaAttributes?.clientId || imageParams?.get('clientId') || undefined,
    // Confluence media nodes use data-collection; standalone images use data-filecollection.
    sourceCollection:
      mediaAttributes?.collection || imageAttributes?.filecollection || undefined,
    // Prefer a declared MIME type, then infer one from the first supported filename.
    mimeType:
      mediaAttributes?.fileMimeType || imageParams?.get('mimeType') || inferredMimeType,
    // Media nodes contain native dimensions; standalone view-mode images use URL values.
    nativeWidth:
      parsePositiveNumber(mediaAttributes?.width) ??
      parsePositiveNumber(imageParams?.get('width')),
    nativeHeight:
      parsePositiveNumber(mediaAttributes?.height) ??
      parsePositiveNumber(imageParams?.get('height')),
    // Media-single containers define display width; standalone images use inline CSS.
    displayWidth:
      parsePositiveNumber(mediaContainerNode?.dataset.width) ??
      parsePositiveNumber(imageNode?.style.width),
  };
};

const parseConfluenceImageNode = (
  node: ConfluenceImageNode,
): ConfluenceImageNodeParseResult => {
  const {
    fileId,
    sourceClientId,
    sourceCollection,
    mimeType,
    nativeWidth,
    nativeHeight,
    displayWidth,
  } = getConfluenceImageAttributes(node);

  if (
    !fileId ||
    !sourceClientId ||
    !mimeType ||
    !nativeWidth ||
    !nativeHeight ||
    !isValidUuid(sourceClientId) ||
    !isSupportedImageMimeType(mimeType)
  ) {
    return { kind: 'invalid', ...(mimeType ? { mimeType } : {}) };
  }

  const width = displayWidth ?? nativeWidth;
  const height = (width * nativeHeight) / nativeWidth;

  if (!Number.isFinite(height)) {
    return { kind: 'invalid', mimeType };
  }

  return {
    kind: 'image',
    image: {
      fileId,
      sourceClientId,
      sourceCollection,
      mimeType,
      nativeWidth,
      nativeHeight,
      width,
      height,
    },
  };
};

const removeConfluenceMediaNodes = (content: HTMLElement) => {
  content
    .querySelectorAll<HTMLElement>(CONFLUENCE_MEDIA_SELECTOR)
    .forEach((mediaNode) => {
      const mediaSingleNode = mediaNode.closest<HTMLElement>(
        CONFLUENCE_MEDIA_SINGLE_SELECTOR,
      );
      mediaNode.remove();
      if (mediaSingleNode && !mediaSingleNode.textContent?.trim()) {
        mediaSingleNode.remove();
      }
    });
  content
    .querySelectorAll<HTMLImageElement>(CONFLUENCE_RENDERED_IMAGE_SELECTOR)
    .forEach((imageNode) => imageNode.remove());
};

export const parseConfluenceImageFromClipboardHtml = (
  html: string,
): ConfluenceImageParseResult => {
  if (!isFeatureFlagEnabled('slidesFirstClassImages')) {
    return { kind: 'notConfluenceImage' };
  }

  const clipboardDocument = new DOMParser().parseFromString(html, 'text/html');
  const mediaNodes = clipboardDocument.querySelectorAll<HTMLElement>(
    CONFLUENCE_MEDIA_SELECTOR,
  );
  // Read-mode media wrappers contain the same rendered image. Only collect standalone
  // images here so a wrapped image is not parsed twice.
  const renderedImageNodes = Array.from(
    clipboardDocument.querySelectorAll<HTMLImageElement>(
      CONFLUENCE_RENDERED_IMAGE_SELECTOR,
    ),
  ).filter((imageNode) => !imageNode.closest(CONFLUENCE_MEDIA_SELECTOR));

  if (mediaNodes.length === 0 && renderedImageNodes.length === 0) {
    return { kind: 'notConfluenceImage' };
  }

  const confluenceImageNodes: ConfluenceImageNode[] = [
    ...Array.from(mediaNodes).map((mediaNode) => ({
      kind: 'media' as const,
      mediaNode,
      renderedImageNode: mediaNode.querySelector<HTMLImageElement>('img'),
    })),
    ...renderedImageNodes.map((renderedImageNode) => ({
      kind: 'renderedImage' as const,
      renderedImageNode,
    })),
  ];

  const imageResults = confluenceImageNodes.map(parseConfluenceImageNode);
  const images = imageResults.flatMap((result) =>
    result.kind === 'image' ? [result.image] : [],
  );

  const remainingContent = clipboardDocument.body;
  removeConfluenceMediaNodes(remainingContent);

  // Leave payloads containing other media to the normal clipboard pipeline instead of
  // consuming content this parser cannot reproduce.
  if (remainingContent.querySelector(UNSUPPORTED_MIXED_CONTENT_SELECTOR)) {
    return { kind: 'notConfluenceImage' };
  }

  const htmlWithoutImages = remainingContent.innerHTML;
  // Invalid media in a mixed group is intentionally ignored when at least one image can
  // be pasted; unsupported content is only reported when every image is invalid.
  if (images.length === 0) {
    const unsupportedMedia = imageResults.find((result) => result.kind === 'invalid');
    return {
      kind: 'invalidConfluenceImage',
      unsupportedMediaMimeType:
        unsupportedMedia?.kind === 'invalid' && unsupportedMedia.mimeType
          ? unsupportedMedia.mimeType
          : 'application/x-confluence-media',
      htmlWithoutImages,
    };
  }

  return {
    kind: 'confluenceImage',
    images,
    htmlWithoutImages,
  };
};
